import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MapPin, Plus, Search, Trash2, X } from 'lucide-react';

import { countryList } from '../../services/countryList';
import { CountryFlag } from '../../services/countryFlagService';
import { sumGeo } from './affiliateFields';

const fmt = (n) => Number(n || 0).toLocaleString();

/**
 * Multi-country picker with per-country traffic.
 *
 * Writes three columns at once — top_geos plus the organic and paid breakdowns —
 * which is why it replaced the plain Top Geos textarea: keeping those three
 * strings aligned by hand is error-prone, and top_geos_array is generated from
 * top_geos so a malformed list silently breaks every geo filter in the app.
 *
 * The country options come from the API, which derives them from the values
 * already in the table, so a picked name always matches what other rows use.
 */
const GeoTrafficEditor = ({ rows, options, onChange, loadingOptions }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const chosen = useMemo(
    () => new Set(rows.map((r) => r.country.trim().toLowerCase())),
    [rows]
  );

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    return options
      .filter((name) => !chosen.has(name.toLowerCase()))
      .filter((name) => (q ? name.toLowerCase().includes(q) : true))
      .slice(0, 60);
  }, [options, chosen, query]);

  const addCountry = (name) => {
    if (!name || chosen.has(name.trim().toLowerCase())) return;
    onChange([...rows, { country: name.trim(), organic: '', paid: '' }]);
    setQuery('');
    setOpen(false);
  };

  const updateRow = (index, patch) =>
    onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  const removeRow = (index) => onChange(rows.filter((_, i) => i !== index));

  const flagFor = (name) => {
    const code = countryList(name);
    return code ? <CountryFlag countryCode={code} className="h-3.5 w-5" /> : <MapPin className="h-3.5 w-3.5 text-gray-400" />;
  };

  return (
    <div className="rounded-lg border border-gray-200 dark:border-slate-700">
      {/* Add a country */}
      <div ref={boxRef} className="relative border-b border-gray-200 p-2 dark:border-slate-700">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={query}
            onFocus={() => setOpen(true)}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (matches.length > 0) addCountry(matches[0]);
              }
              if (e.key === 'Escape') setOpen(false);
            }}
            placeholder={loadingOptions ? 'Loading countries…' : 'Add a country…'}
            className="w-full rounded-lg border border-gray-200 bg-white py-1.5 pl-9 pr-8 text-sm text-gray-900 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-slate-200"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {open && (
          <div className="absolute left-2 right-2 top-full z-10 mt-1 max-h-56 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg dark:border-slate-700 dark:bg-slate-800">
            {matches.length === 0 ? (
              <div className="px-3 py-2.5 text-xs text-gray-400 dark:text-slate-500">
                {loadingOptions ? 'Loading…' : 'No matching country'}
              </div>
            ) : (
              matches.map((name) => (
                <button
                  key={name}
                  type="button"
                  onClick={() => addCountry(name)}
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-blue-50 dark:text-slate-300 dark:hover:bg-slate-700"
                >
                  <Plus className="h-3 w-3 flex-shrink-0 text-gray-400" />
                  {name}
                </button>
              ))
            )}
          </div>
        )}
      </div>

      {/* Selected countries */}
      {rows.length === 0 ? (
        <p className="px-3 py-4 text-center text-xs text-gray-400 dark:text-slate-500">
          No countries yet — search above to add one.
        </p>
      ) : (
        <>
          <div className="grid grid-cols-[1fr_92px_92px_32px] items-center gap-2 border-b border-gray-100 px-3 py-1.5 text-[10px] font-medium uppercase tracking-wide text-gray-400 dark:border-slate-800 dark:text-slate-500">
            <span>Country</span>
            <span className="text-right">Organic</span>
            <span className="text-right">Paid</span>
            <span />
          </div>

          <div className="max-h-64 divide-y divide-gray-100 overflow-y-auto dark:divide-slate-800">
            {rows.map((row, index) => (
              <div
                key={`${row.country}-${index}`}
                className="grid grid-cols-[1fr_92px_92px_32px] items-center gap-2 px-3 py-2"
              >
                <span className="flex min-w-0 items-center gap-2 text-sm text-gray-800 dark:text-slate-200">
                  {flagFor(row.country)}
                  <span className="truncate" title={row.country}>
                    {row.country}
                  </span>
                </span>

                <input
                  type="number"
                  min="0"
                  value={row.organic}
                  onChange={(e) => updateRow(index, { organic: e.target.value })}
                  placeholder="—"
                  className="w-full rounded border border-gray-200 bg-white px-2 py-1 text-right text-xs tabular-nums text-gray-900 outline-none focus:border-blue-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                />
                <input
                  type="number"
                  min="0"
                  value={row.paid}
                  onChange={(e) => updateRow(index, { paid: e.target.value })}
                  placeholder="—"
                  className="w-full rounded border border-gray-200 bg-white px-2 py-1 text-right text-xs tabular-nums text-gray-900 outline-none focus:border-amber-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                />

                <button
                  type="button"
                  onClick={() => removeRow(index)}
                  title={`Remove ${row.country}`}
                  className="justify-self-center rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20 dark:hover:text-red-400"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-[1fr_92px_92px_32px] items-center gap-2 border-t border-gray-200 px-3 py-1.5 text-[11px] text-gray-500 dark:border-slate-700 dark:text-slate-400">
            <span>{rows.length} {rows.length === 1 ? 'country' : 'countries'}</span>
            <span className="text-right tabular-nums">{fmt(sumGeo(rows, 'organic'))}</span>
            <span className="text-right tabular-nums">{fmt(sumGeo(rows, 'paid'))}</span>
            <span />
          </div>
        </>
      )}
    </div>
  );
};

export default GeoTrafficEditor;
